import { useSyncExternalStore } from "react";

import { DEFAULT_MIVO_LOCALE } from "./ITranslation";
import i18next from "./i18n";
import {
  readLocaleFromSearch,
  readMarketCountryFromSearch,
  stripLocaleFromUrl,
} from "./localeBootstrap";
import { writeStoredLocale } from "./localeStorage";
import { DEFAULT_MARKET_COUNTRY } from "./marketCountry";
import { writeStoredMarketCountry } from "./marketCountryStorage";

let isMultiLanguageEnabled = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function getIsMultiLanguageEnabled(): boolean {
  return isMultiLanguageEnabled;
}

export function setIsMultiLanguageEnabled(enabled: boolean): void {
  if (isMultiLanguageEnabled === enabled) {
    return;
  }
  isMultiLanguageEnabled = enabled;
  emit();
}

export function subscribeIsMultiLanguageEnabled(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useIsMultiLanguageEnabled(): boolean {
  return useSyncExternalStore(subscribeIsMultiLanguageEnabled, getIsMultiLanguageEnabled, () => false);
}

export function forceDefaultLocaleAndCountry(): void {
  writeStoredLocale(DEFAULT_MIVO_LOCALE);
  writeStoredMarketCountry(DEFAULT_MARKET_COUNTRY);
  void i18next.changeLanguage(DEFAULT_MIVO_LOCALE);
}

export async function fetchIsMultiLanguageEnabled(apiBase = "/api"): Promise<boolean> {
  try {
    const base = apiBase.replace(/\/$/, "");
    const response = await fetch(`${base}/public/system-settings`);
    if (!response.ok) {
      return false;
    }
    const json = (await response.json()) as { isMultiLanguageEnabled?: unknown };
    return json.isMultiLanguageEnabled === true;
  } catch {
    return false;
  }
}

/** Load the public flag, then apply URL handoff (ON) or force pt-BR/BR (OFF). */
export async function bootstrapMultiLanguage(
  loadEnabled: () => Promise<boolean> = () => fetchIsMultiLanguageEnabled(),
): Promise<boolean> {
  let enabled = false;
  try {
    enabled = await loadEnabled();
  } catch {
    enabled = false;
  }

  setIsMultiLanguageEnabled(enabled);

  const localeFromUrl = readLocaleFromSearch();
  const countryFromUrl = readMarketCountryFromSearch();
  stripLocaleFromUrl();

  if (enabled) {
    if (localeFromUrl) {
      void i18next.changeLanguage(localeFromUrl);
      writeStoredLocale(localeFromUrl);
    }
    if (countryFromUrl) {
      writeStoredMarketCountry(countryFromUrl);
    }
  } else {
    forceDefaultLocaleAndCountry();
  }

  return enabled;
}
